import React, { useState } from 'react';
import cx from 'clsx';
import GoogleMapReact from 'google-map-react';
import type { Coords, Size, Bounds, MapOptions } from 'google-map-react';
import { AutoCompletePlaces } from './AutoCompletePlaces';
import { AppErrorBoundary } from '../AppErrorBoundary';
import activePin from './assets/pin-active.png'
import InactivePin from './assets/pin-inactive.png'
export interface GoogleMapProps {
  apiKey: string;
  children?: React.ReactNode;
  zoom?: number;
  center?: Coords;
  onAPILoaded?(maps: { map: any; maps: any; ref: Element | null }): void;
  mapOptions?: MapOptions;
  libraries?: string[] | string | undefined;
  wrapperClassName?: string;
  showMarker?: boolean;
  showPlacesAutoComplete?: boolean;
  onLatLngChanged?(latLng: Coords, address?: any): any;
  markerImage?: string;
  countryCode?: string;
  searchInputName?: string;
  searchInputPlaceholder?: string;
  defaultToMyLocation?: boolean;
  markerList: any[]
}

export const GoogleMap: React.FC<GoogleMapProps> = ({
  apiKey,
  zoom = 13,
  center,
  onAPILoaded,
  libraries = ['places'],
  wrapperClassName,
  showMarker = false,
  showPlacesAutoComplete = true,
  searchInputName = 'SearchAutoCompleteInputName',
  searchInputPlaceholder = 'Search',
  onLatLngChanged,
  markerImage = '',
  mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    mapTypeId: 'terrain',
  },
  countryCode = 'sa',
  defaultToMyLocation = false,
  markerList = []
}) => {
  const defaultCenter = {
    lat: 24.774265,
    lng: 46.738586,
  };

  const autoCompleteOptions = {
    componentRestrictions: { country: countryCode },
    fields: ['name', 'geometry.location', 'geometry.viewport', 'place_id'],
  };

  const [currentZoom, setCurrentZoom] = useState<number>(zoom);
  const [googleMap, setGoogleMap] = useState<any>();

  const setMarkerToCenter = (
    latLng: Coords,
    placeName?: string,
    callOnChange: boolean = false
  ) => {
    if (callOnChange && onLatLngChanged) {
      onLatLngChanged(latLng, placeName);
    }
  };

  const addAutoCompleteInput = (gMaps: any, gMap: any) => {
    const input = document.getElementById(searchInputName) as HTMLInputElement;
    const autocomplete = new gMaps.places.Autocomplete(
      input,
      autoCompleteOptions
    );
    autocomplete.bindTo('bounds', gMap);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) {
        return;
      }
      // If the place has a geometry, then present it on a map.
      gMap.setCenter(place.geometry.location);
      const latLng = place.geometry.location.toJSON();
      setMarkerToCenter(latLng, place.name, true);
    });
  };




  const handleBoundsChange = (changeValue: {
    center: Coords;
    zoom: number;
    bounds: Bounds;
    marginBounds: Bounds;
    size: Size;
  }) => {
    // Handle bounds change here if needed
    setCurrentZoom(zoom)
  };

  const renderMarkers = (map: any, maps: any, markerList: any[]) => {
    if (!markerList) return; // Add null check for markerList
    const activePinMarker = {
      url: activePin.src, // Path to the local image
      scaledSize: new maps.Size(40, 40), // Adjust the size as needed
      origin: new maps.Point(0, 0), // Adjust the origin point as needed
      anchor: new maps.Point(20, 40), // Adjust the anchor point as needed
    };
    const inActivePinMarker = {
      url: InactivePin.src, // Path to the local image
      scaledSize: new maps.Size(40, 40), // Adjust the size as needed
      origin: new maps.Point(0, 0), // Adjust the origin point as needed
      anchor: new maps.Point(20, 40), // Adjust the anchor point as needed
    };
    markerList.forEach(markerData => {
      if (!markerData) return; // Add null check for markerData

      const marker = new maps.Marker({
        position: { lat: markerData.latitude, lng: markerData.longitude },
        map,
        title: markerData.name,
        icon: markerData?.isActive ? activePinMarker : inActivePinMarker
      });

      // Create an info window for this marker
      const infoWindow = new maps.InfoWindow({
        content: `<div>${markerData.name}</div>`, // Content of the info window
      });

      // Add click event listener to marker to open info window
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
    });
  }
  const handleApiLoaded = ({
    map,
    maps,
    ref,
  }: {
    map: any;
    maps: any;
    ref: Element | null;
  }) => {
    if (onAPILoaded) {
      onAPILoaded({ map, maps, ref });
    }
    if (showMarker && center && center.lat) {
      addAutoCompleteInput(maps, map);
    }
    setGoogleMap(map);

    // Call renderMarkers function here
    renderMarkers(map, maps, markerList);
  };



  return (
    <div
      className={cx(
        'relative flex h-full w-full flex-1',
        wrapperClassName
      )}
      data-testid='TestId__GoogleMap'
    >
      <AppErrorBoundary>
        {showPlacesAutoComplete && (
          <AutoCompletePlaces
            searchInputName={searchInputName}
            searchInputPlaceholder={searchInputPlaceholder}
          />
        )}
      </AppErrorBoundary>
      <AppErrorBoundary>
        {(currentZoom > zoom || currentZoom < zoom || currentZoom === zoom) ? (<>
          <GoogleMapReact
            bootstrapURLKeys={{ key: apiKey, libraries, region: 'sa' }}
            center={defaultCenter}
            defaultCenter={defaultCenter}
            zoom={zoom}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleApiLoaded}
            onChange={handleBoundsChange}
            options={mapOptions}
          >
          </GoogleMapReact>
        </>) : null}
      </AppErrorBoundary>
    </div>
  );
};
